import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import {
  AdminPanelTitle,
  AdminTabButton,
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminStatusBadge,
  AdminFlagStatusBadge,
  AdminDetailTableHeader,
  AdminTableBody,
  AdminSupportingText,
  AdminMetadataText,
  AdminMainText,
  AdminLinkAction,
  AdminStandardAvatar,
  ADMIN_TAB_CONTENT_CONTAINER_CLASS,
  ADMIN_TAB_CONTENT_OFFSET_CLASS,
  ADMIN_DETAIL_MAIN_HEADER_CLASS,
  ADMIN_DETAIL_TABLE_CLASS,
} from '../shared/adminUx';
import ConfigurationListPaginationFooter from '../../../components/AdminDashboard/Configuration/ConfigurationListPaginationFooter';
import { CONFIG_LIST_PAGE_SIZE } from '../../../utils/configurationTableUx';
import Profile from '../../../assets/img/User.png';
import BusinessFallback from '../../../assets/img/default-image.jpg';
import { getReviewsForScenarios } from '../../../data/reviewModerationScenarios';
import { recordReviewActivity } from '../../../data/changeHistoryAudit';

function normalizeFlagSource(raw, ctx = {}) {
  const from = String(raw || ctx.source || '').trim();
  const type = String(ctx.type || ctx.category || '').toLowerCase();
  const lowered = from.toLowerCase();

  if (from === 'User Flag' || from === 'System Flag' || from === 'Team Flag') return from;

  if (
    lowered === 'user flag' ||
    lowered === 'customer' ||
    lowered === 'customer flag' ||
    lowered === 'business' ||
    lowered === 'business flag' ||
    lowered === 'user (business)' ||
    lowered === 'user (customer)' ||
    lowered.includes('user report') ||
    lowered.includes('user flag') ||
    type.includes('user report')
  ) {
    return 'User Flag';
  }

  if (
    lowered === 'team flag' ||
    lowered === 'admin' ||
    lowered === 'support ticket' ||
    lowered.includes('moderator') ||
    lowered.includes('team') ||
    lowered.includes('support') ||
    ctx.raisedBy === 'Admin' ||
    type.includes('moderation case') ||
    type.includes('fraud') ||
    type.includes('security') ||
    type.includes('compliance')
  ) {
    return 'Team Flag';
  }

  if (
    lowered === 'system flag' ||
    lowered === 'system' ||
    lowered === 'auto' ||
    type.includes('auto-flag') ||
    type.includes('system flag') ||
    type.includes('content control')
  ) {
    return 'System Flag';
  }

  return 'System Flag';
}

function normalizeReviewTarget(raw) {
  const s = String(raw ?? '').trim().toLowerCase();
  if (
    s === 'business reply' ||
    s === 'reply' ||
    s === 'review reply' ||
    s.includes('reply')
  ) {
    return 'Business Reply';
  }
  return 'Customer Review';
}

function formatModerationCaseId(id) {
  if (!id) return null;
  const s = String(id).trim();
  if (/^MOA-/i.test(s)) return s.toUpperCase();
  if (/^MOD-DEC-/i.test(s)) return s.toUpperCase();
  if (/^(?:MODS|MOD)-/i.test(s)) {
    const digits = s.replace(/^(?:MODS|MOD)-?/i, '').replace(/\D/g, '');
    if (!digits) return s.toUpperCase();
    return `MOD-${digits.padStart(6, '0')}`;
  }
  const digits = s.replace(/\D/g, '');
  if (!digits) return s;
  return `MOD-${digits.padStart(6, '0')}`;
}

function normalizeHandlingStatus(raw, hasLinkedCase) {
  const v = String(raw || '').trim();
  if (v === 'New' || v === 'Active' || v === 'Decided') return v;
  // Legacy case statuses → Reviews Handling Status (New · Active · Decided)
  if (['Closed', 'Resolved', 'Cleared', 'Escalated', 'Approved', 'Decided'].includes(v)) {
    return 'Decided';
  }
  if (v === 'Under Review' || v === 'In Review' || v === 'Open' || v === 'Attached' || v === 'Pending') {
    return hasLinkedCase || v !== 'Open' ? 'Active' : 'New';
  }
  if (hasLinkedCase) return 'Active';
  return v || '—';
}

function normalizeFlagSeverity(raw) {
  let s = String(raw ?? 'Medium').trim();
  if (s === 'Critical') s = 'High';
  if (!['Low', 'Medium', 'High'].includes(s)) return 'Medium';
  return s;
}

function normalizeFlagId(id, index = 0) {
  if (!id) return `FLG-${String(31000 + index).padStart(5, '0')}`;
  if (typeof id === 'string' && id.startsWith('FLG-')) return id;
  const match = id.toString().match(/(\d+)/);
  if (match) return `FLG-${String(match[1]).padStart(5, '0')}`;
  const hash = id.toString().split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return `FLG-${String((hash % 99999) + 10000).padStart(5, '0')}`;
}

/** Flag ID | Case ID | Review Target | Source | Reason | Severity | Case Status | Created On | Last Updated | Action */
const FLAGS_TABLE_GRID =
  'minmax(0, 0.7fr) minmax(0, 0.9fr) minmax(0, 0.95fr) minmax(0, 0.85fr) minmax(0, 1.15fr) minmax(0, 0.5fr) minmax(0, 0.85fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)';

function buildReviewFlagRows(review) {
  const fallbackCaseId =
    review.linkedCaseId ||
    review.moderationCases?.[0]?.caseId ||
    null;

  return (review.flags || []).map((item, index) => {
    const flagId = normalizeFlagId(item.flagId || item.caseId || item.id, index);
    const linkedCaseRaw =
      item.linkedCaseId ||
      item.caseId ||
      item.handlingRecordId ||
      (item.type === 'Moderation Case' || item.isCase ? item.id : null) ||
      fallbackCaseId;
    const caseId = linkedCaseRaw ? formatModerationCaseId(linkedCaseRaw) : null;
    const handlingRecord = caseId || '—';
    const flagClosed = /^(closed|resolved|cleared)$/i.test(
      String(item.status || item.flagStatus || '').trim()
    );
    const handlingStatus = normalizeHandlingStatus(
      item.handlingStatus ||
        item.caseStatus ||
        (flagClosed ? 'Decided' : null) ||
        review.moderationCases?.[0]?.status ||
        item.status ||
        item.flagStatus,
      Boolean(caseId)
    );

    return {
      key: `${flagId}-${index}`,
      flagId,
      reviewTarget: normalizeReviewTarget(
        item.reviewTarget || item.target || item.flagTarget
      ),
      source: normalizeFlagSource(item.raisedBy || item.source, item),
      reasonText: (item.reason || item.trigger || item.object || '').trim() || '—',
      severity: normalizeFlagSeverity(item.severity),
      handlingRecord,
      handlingRecordId: caseId,
      handlingStatus,
      createdAt: item.createdAt || item.openedOn || review.postedAt,
      updatedAt:
        item.lastUpdated || item.updatedAt || item.createdAt || item.openedOn || review.postedAt,
      actionLabel: caseId ? 'Open Case' : null,
      actionPath: caseId
        ? `/admin/moderation/queue/${encodeURIComponent(caseId)}`
        : null,
    };
  });
}

function getDetailTablePagination(totalCount, currentPage) {
  const totalPages = Math.max(1, Math.ceil(totalCount / CONFIG_LIST_PAGE_SIZE));
  const effectivePage = Math.min(currentPage, totalPages);
  const startIndex = (effectivePage - 1) * CONFIG_LIST_PAGE_SIZE;
  return {
    totalPages,
    effectivePage,
    rangeStart: totalCount === 0 ? 0 : startIndex + 1,
    rangeEnd: Math.min(effectivePage * CONFIG_LIST_PAGE_SIZE, totalCount),
    showPagination: totalCount > CONFIG_LIST_PAGE_SIZE,
    paginate: (items) => items.slice(startIndex, startIndex + CONFIG_LIST_PAGE_SIZE),
  };
}

const getFlagSeverityTextClass = (severity) => {
  const s = String(severity || '').trim();
  if (s === 'High' || s === 'Critical' || s === 'Severe') return 'text-alertred';
  if (s === 'Medium') return 'text-warmyellow';
  if (s === 'Low') return 'text-freshgreen';
  return '';
};

const ReviewDetail = () => {
  const { reviewId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [activeTab, setActiveTab] = useState(location.state?.activeTab || 'overview');
  const [flagsCurrentPage, setFlagsCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [review, setReview] = useState(null);
  const [reviewIdCopied, setReviewIdCopied] = useState(false);
  const [copiedOverviewField, setCopiedOverviewField] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  
  // Notes modal state
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [pinnedNotes, setPinnedNotes] = useState(new Set());
  const [noteRowActionsOpen, setNoteRowActionsOpen] = useState({});
  const noteRowActionsRefs = useRef({});
  const [noteForm, setNoteForm] = useState({
    category: '',
    noteText: ''
  });

  useEffect(() => {
    if (location.state?.activeTab && ['overview', 'thread', 'flags'].includes(location.state.activeTab)) {
      setActiveTab(location.state.activeTab);
    }
  }, [location.state?.activeTab]);

  useEffect(() => {
    setFlagsCurrentPage(1);
  }, [reviewId]);

  useEffect(() => {
    if (!review?.flags?.length) return;
    const pages = Math.max(1, Math.ceil(review.flags.length / CONFIG_LIST_PAGE_SIZE));
    setFlagsCurrentPage((p) => Math.min(p, pages));
  }, [review?.flags?.length]);

  useEffect(() => {
    const fetchReview = async () => {
      setLoading(true);
      await new Promise((resolve) => setTimeout(resolve, 250));

      let foundReview = null;
      if (reviewId) {
        const scenarios = getReviewsForScenarios();
        foundReview = scenarios.find(r => String(r.reviewId).toLowerCase() === String(reviewId).toLowerCase() || String(r.id).toLowerCase() === String(reviewId).toLowerCase()) || null;
      }
      if (!foundReview && location.state?.review) {
        foundReview = location.state.review;
      }

      if (foundReview) {
        setReview(foundReview);
      } else {
        // If no scenario available, show not found (we avoid using fabricated review data)
        setReview(null);
      }
      setLoading(false);
    };

    fetchReview();
  }, [reviewId, location]);

  // Notes handlers
  const openNotesModal = (note = null) => {
    if (note) {
      setEditingNote(note);
      setNoteForm({
        category: note.category || '',
        noteText: note.noteContent || note.content || ''
      });
    } else {
      setEditingNote(null);
      setNoteForm({
        category: '',
        noteText: ''
      });
    }
    setNotesModalOpen(true);
  };

  const closeNotesModal = () => {
    setNotesModalOpen(false);
    setEditingNote(null);
    setNoteForm({
      category: '',
      noteText: ''
    });
  };

  const handleNoteSave = () => {
    if (!noteForm.category || !noteForm.noteText) {
      alert('Category and Note text are required');
      return;
    }

    const now = new Date();
    const reviewRef = review?.reviewId || review?.id || reviewId;
    
    if (editingNote) {
      setReview(prev => ({
        ...prev,
        notes: prev.notes.map(note => 
          note.id === editingNote.id 
            ? {
                ...note,
                category: noteForm.category,
                noteContent: noteForm.noteText,
                content: noteForm.noteText
              }
            : note
        )
      }));
      if (reviewRef) {
        try {
          recordReviewActivity({
            reviewRef,
            changeType: 'Note updated',
            activityDetails: `Rachel Brown; ${noteForm.category}`,
            changedBy: 'Rachel Brown',
          });
        } catch {
          /* history recording failure must not reverse the source save */
        }
      }
    } else {
      const newNote = {
        id: `note-${Date.now()}`,
        createdAt: now.toISOString(),
        adminName: 'Moderator — Rachel Brown',
        author: 'Rachel Brown',
        category: noteForm.category,
        noteContent: noteForm.noteText,
        content: noteForm.noteText
      };
      
      setReview(prev => ({
        ...prev,
        notes: [newNote, ...prev.notes]
      }));
      if (reviewRef) {
        try {
          recordReviewActivity({
            reviewRef,
            changeType: 'Note added',
            activityDetails: `Rachel Brown; ${noteForm.category}`,
            changedBy: 'Rachel Brown',
          });
        } catch {
          /* history recording failure must not reverse the source save */
        }
      }
    }
    
    closeNotesModal();
  };

  const handleNoteDelete = (noteId) => {
    if (window.confirm('Delete this note?')) {
      setReview(prev => ({
        ...prev,
        notes: prev.notes.filter(note => note.id !== noteId)
      }));
      // Remove from pinned notes if it was pinned
      setPinnedNotes(prev => {
        const newSet = new Set(prev);
        newSet.delete(noteId);
        return newSet;
      });
    }
  };

  const handleNotePin = (noteId) => {
    setPinnedNotes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(noteId)) {
        newSet.delete(noteId);
      } else {
        newSet.add(noteId);
      }
      return newSet;
    });
    setNoteRowActionsOpen(prev => ({ ...prev, [noteId]: false }));
  };

  const toggleNoteRowActions = (noteId) => {
    setNoteRowActionsOpen((prev) => {
      if (prev[noteId]) return {};
      return { [noteId]: true };
    });
  };

  // Handle clicking outside to close note row actions
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutsideAll = Object.values(noteRowActionsRefs.current).every(ref => {
        const el = ref;
        return !el || !el.contains(event.target);
      });

      if (isOutsideAll) {
        setNoteRowActionsOpen({});
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  const formatDate = (isoString) => {
    if (!isoString) return '—';
    const date = new Date(isoString);
    return date.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  /** Header / list style — e.g. "5 Aug 2026, 2:15 pm" */
  const formatHeaderDateTime = (isoString) => {
    if (!isoString) return '—';
    const date = new Date(isoString);
    if (Number.isNaN(date.getTime())) return '—';
    const datePart = date.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
    const timePart = date.toLocaleTimeString('en-AU', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
    return `${datePart}, ${timePart}`;
  };

  /** Flags tab — e.g. "31 Jan 2026, 1:10 am" */
  const formatFlagsDateTime = (isoString) => formatHeaderDateTime(isoString);

  // Viewing note state for Admin Notes tab
  const [viewingNote, setViewingNote] = useState(null);

  const closeViewNoteModal = () => {
    setViewingNote(null);
  };

  const handleViewNote = (note) => {
    setViewingNote(note);
    setNoteRowActionsOpen(prev => ({ ...prev, [note.id]: false }));
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <AdminSupportingText className="text-mediumgray">Loading review details…</AdminSupportingText>
        </div>
      </AdminLayout>
    );
  }

  if (!review) {
    return (
      <AdminLayout>
        <DashboardMainContainer className="!bg-white">
          <div className="text-center space-y-4 py-16">
            <AdminPanelTitle className="text-alertred">
              Review not found
            </AdminPanelTitle>
            <AdminSupportingText className="text-mediumgray">
              We couldn’t locate that review. It might have been removed or the link is outdated.
            </AdminSupportingText>
            <AdminLinkAction type="button" onClick={() => navigate('/admin/reviews/all')}>
              Back to all reviews
            </AdminLinkAction>
          </div>
        </DashboardMainContainer>
      </AdminLayout>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'thread', label: 'Review & Reply' },
    { id: 'flags', label: 'Flags' },
  ];

  const openFlagsCount =
    typeof review.openFlagsCount === 'number'
      ? review.openFlagsCount
      : (review.flags || []).filter((f) => {
          const st = String(f.caseStatus || f.status || '').trim();
          return st === 'New' || st === 'Active';
        }).length;

  const displayReviewId = review.reviewId
    ? review.reviewId.startsWith('RVW-')
      ? review.reviewId
      : `RVW-${String(review.reviewId).replace(/^RV-?/i, '')}`
    : '—';

  const reviewFlagRows = buildReviewFlagRows(review);
  const flagsTablePagination = getDetailTablePagination(reviewFlagRows.length, flagsCurrentPage);
  const paginatedFlagRows = flagsTablePagination.paginate(reviewFlagRows);

  const reviewImageCount = Array.isArray(review.attachments)
    ? review.attachments.length
    : Number(review.imageCount) || 0;

  const editUntilDisplay = (() => {
    if (review.editUntil) return formatHeaderDateTime(review.editUntil);
    const posted = review.postedAt || review.submittedAt;
    if (!posted) return '—';
    const d = new Date(posted);
    if (Number.isNaN(d.getTime())) return '—';
    d.setUTCDate(d.getUTCDate() + 30);
    return formatHeaderDateTime(d.toISOString());
  })();

  const interactionTypeDisplay = (() => {
    const raw = String(review.interactionType || '').trim();
    if (!raw) return '—';
    if (raw === 'Quote' || raw.toLowerCase().startsWith('quote')) return 'Quote';
    if (raw === 'Message' || raw.toLowerCase().startsWith('message')) return 'Message';
    return raw;
  })();

  const interactionIdDisplay = review.interactionId || null;

  const isMessageInteraction =
    interactionTypeDisplay === 'Message' ||
    String(interactionIdDisplay || '').startsWith('MSG-');

  const interactionViewPath = interactionIdDisplay
    ? isMessageInteraction
      ? `/admin/communication/all/${encodeURIComponent(interactionIdDisplay)}`
      : `/admin/quotes/all/${encodeURIComponent(interactionIdDisplay)}`
    : null;

  const customerIdDisplay = (() => {
    const custId = review.reviewer?.id || review.reviewer?.customerId || review.customerId;
    if (!custId) return null;
    return String(custId).startsWith('CUS-')
      ? custId
      : `CUS-${String(custId).replace(/\D/g, '').slice(-4).padStart(4, '0')}`;
  })();

  const businessIdDisplay = (() => {
    const bid = review.business?.id || review.business?.businessId || review.businessId;
    if (!bid) return null;
    return String(bid).startsWith('BUS-')
      ? bid
      : `BUS-${String(bid).replace(/\D/g, '').slice(-4).padStart(4, '0')}`;
  })();

  const customerStatusDisplay = (() => {
    const customerStatus = review.reviewer?.status || review.customerStatus || 'Active';
    if (customerStatus === 'Active' || customerStatus === 'active') return 'Active';
    if (customerStatus === 'Blocked' || customerStatus === 'blocked') return 'Blocked';
    return customerStatus;
  })();

  const customerVerificationDisplay = (() => {
    const raw =
      review.reviewer?.verificationStatus ||
      review.verificationStatus ||
      'Verified';
    const s = String(raw).trim();
    if (!s) return 'Verified';
    if (s.toLowerCase() === 'verified') return 'Verified';
    if (s.toLowerCase() === 'pending' || s.toLowerCase() === 'verification pending')
      return 'Verification Pending';
    return s;
  })();

  const businessStatusDisplay = (() => {
    const businessStatus = review.business?.status || 'Active';
    if (businessStatus === 'Active' || businessStatus === 'active') return 'Active';
    if (businessStatus === 'Blocked' || businessStatus === 'blocked') return 'Blocked';
    return businessStatus;
  })();

  const businessReplyStatus =
    review.replyStatus ||
    (review.reply?.status === 'Deleted'
      ? 'Deleted'
      : review.reply?.hasReply && (review.reply?.body || review.reply?.timestamp)
        ? 'Replied'
        : 'Not Replied');

  const replyIdDisplay = (() => {
    if (!(review.reply?.hasReply || review.reply?.status === 'Deleted')) return null;
    const raw = review.reply?.replyId || review.reply?.reviewReplyId;
    if (!raw) return null;
    return String(raw).startsWith('RRP-')
      ? raw
      : `RRP-${String(raw).replace(/\D/g, '').padStart(6, '0')}`;
  })();

  const customerNameDisplay = review.reviewer?.name || review.customerName || '—';
  const businessNameDisplay = review.business?.name || review.businessName || '—';
  const ratingValue =
    Number.isFinite(review.rating) && review.rating > 0
      ? Number.isInteger(review.rating)
        ? review.rating
        : Math.round(Number(review.rating))
      : null;
  const handleCopyReviewId = async () => {
    if (!displayReviewId || displayReviewId === '—') return;
    try {
      await navigator.clipboard.writeText(displayReviewId);
      setReviewIdCopied(true);
      window.setTimeout(() => setReviewIdCopied(false), 1500);
    } catch {
      // Clipboard unavailable — leave UI unchanged
    }
  };

  const handleCopyOverviewField = async (fieldKey, value) => {
    if (!value || value === '—') return;
    try {
      await navigator.clipboard.writeText(String(value));
      setCopiedOverviewField(fieldKey);
      window.setTimeout(() => setCopiedOverviewField(null), 1500);
    } catch {
      // Clipboard unavailable — leave UI unchanged
    }
  };

  const renderCopyableOverviewValue = (fieldKey, value, label) => {
    const display = value || '—';
    const canCopy = Boolean(value && value !== '—');
    return (
      <div className="flex items-center gap-2 min-w-0">
        <AdminDetailFieldValue as="div" className="truncate min-w-0">
          {display}
        </AdminDetailFieldValue>
        {canCopy ? (
          <button
            type="button"
            onClick={() => handleCopyOverviewField(fieldKey, value)}
            className="inline-flex items-center justify-center shrink-0 text-mediumgray hover:text-primaryblue"
            title={copiedOverviewField === fieldKey ? 'Copied' : `Copy ${label}`}
            aria-label={copiedOverviewField === fieldKey ? 'Copied' : `Copy ${label}`}
          >
            <i
              className={`fi ${
                copiedOverviewField === fieldKey ? 'fi-rr-check text-freshgreen' : 'fi-rr-copy'
              } text-xs leading-none`}
              aria-hidden
            />
          </button>
        ) : null}
      </div>
    );
  };

  const renderOverviewLinkedField = ({ label, valueText, enabled, to, state, onNavigate }) => (
    <div className="space-y-1">
      <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
      {enabled ? (
        <AdminDetailFieldValue>
          {to ? (
            <AdminLinkAction to={to} state={state} className="tabular-nums text-left">
              {valueText}
            </AdminLinkAction>
          ) : (
            <AdminLinkAction type="button" onClick={onNavigate} className="tabular-nums text-left">
              {valueText}
            </AdminLinkAction>
          )}
        </AdminDetailFieldValue>
      ) : (
        <AdminDetailFieldValue>—</AdminDetailFieldValue>
      )}
    </div>
  );

  const handleViewPublicProfile = () => {
    if (!businessIdDisplay) return;
    window.open(
      `/poster-profile/?id=${encodeURIComponent(businessIdDisplay)}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const handleFlagRowAction = (flag) => {
    if (!flag?.actionPath) return;
    navigate(flag.actionPath, {
      state: {
        from: location.pathname,
        flagId: flag.flagId,
        handlingRecordId: flag.handlingRecordId,
        reviewId: displayReviewId,
      },
    });
  };

  return (
    <AdminLayout>
      <div className="tp-container-main">
      <div>
        {/* Persistent Review Header — title + CTA only */}
        <DashboardMainContainer className={ADMIN_DETAIL_MAIN_HEADER_CLASS}>
          <AdminPanelTitle>Review Detail</AdminPanelTitle>
        </DashboardMainContainer>

        <DashboardMainContainer className="!bg-white">
          <div className="border-b border-lightgray">
            <div className="flex space-x-8 overflow-x-auto">
              {tabs.map((tab) => (
                <AdminTabButton key={tab.id} active={activeTab === tab.id} onClick={() => setActiveTab(tab.id)}>
                  {tab.label}
                </AdminTabButton>
              ))}
            </div>
          </div>

          <div
            className={
              activeTab === 'flags'
                ? `${ADMIN_TAB_CONTENT_OFFSET_CLASS} w-full min-w-0`
                : ADMIN_TAB_CONTENT_CONTAINER_CLASS
            }
          >
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <div>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {/* Column 1 — Customer */}
                    <div className="space-y-4">
                      <div className="space-y-1">
                        <AdminDetailFieldLabel>Customer ID</AdminDetailFieldLabel>
                        {renderCopyableOverviewValue('customerId', customerIdDisplay, 'Customer ID')}
                      </div>
                      <div className="space-y-1">
                        <AdminDetailFieldLabel>Customer Name</AdminDetailFieldLabel>
                        <AdminDetailFieldValue>{customerNameDisplay}</AdminDetailFieldValue>
                      </div>
                      <div className="space-y-1">
                        <AdminDetailFieldLabel>Verification Status</AdminDetailFieldLabel>
                        {customerVerificationDisplay ? (
                          <AdminStatusBadge
                            status={customerVerificationDisplay}
                            label={customerVerificationDisplay}
                          />
                        ) : (
                          <AdminDetailFieldValue>—</AdminDetailFieldValue>
                        )}
                      </div>
                      <div className="space-y-1">
                        <AdminDetailFieldLabel>Account Status</AdminDetailFieldLabel>
                        {customerStatusDisplay ? (
                          <AdminStatusBadge
                            status={customerStatusDisplay}
                            label={customerStatusDisplay}
                          />
                        ) : (
                          <AdminDetailFieldValue>—</AdminDetailFieldValue>
                        )}
                      </div>
                      {renderOverviewLinkedField({
                        label: 'Customer',
                        valueText: 'View',
                        enabled: Boolean(customerIdDisplay),
                        to: customerIdDisplay
                          ? `/admin/users/customers/${encodeURIComponent(customerIdDisplay)}`
                          : undefined,
                        state: { customer: review.reviewer },
                      })}
                    </div>

                    {/* Column 2 — Business */}
                    <div className="space-y-4">
                      <div className="space-y-1">
                        <AdminDetailFieldLabel>Business ID</AdminDetailFieldLabel>
                        {renderCopyableOverviewValue('businessId', businessIdDisplay, 'Business ID')}
                      </div>
                      <div className="space-y-1">
                        <AdminDetailFieldLabel>Business Name</AdminDetailFieldLabel>
                        <AdminDetailFieldValue>{businessNameDisplay}</AdminDetailFieldValue>
                      </div>
                      <div className="space-y-1">
                        <AdminDetailFieldLabel>Account Status</AdminDetailFieldLabel>
                        {businessStatusDisplay ? (
                          <AdminStatusBadge
                            status={businessStatusDisplay}
                            label={businessStatusDisplay}
                          />
                        ) : (
                          <AdminDetailFieldValue>—</AdminDetailFieldValue>
                        )}
                      </div>
                      {renderOverviewLinkedField({
                        label: 'Business',
                        valueText: 'View',
                        enabled: Boolean(businessIdDisplay),
                        to: businessIdDisplay
                          ? `/admin/users/businesses/${encodeURIComponent(businessIdDisplay)}`
                          : undefined,
                        state: { business: review.business },
                      })}
                      {renderOverviewLinkedField({
                        label: 'Public Profile',
                        valueText: 'View',
                        enabled: Boolean(businessIdDisplay),
                        onNavigate: handleViewPublicProfile,
                      })}
                    </div>

                    {/* Column 3 — Qualifying Interaction */}
                    <div className="space-y-4">
                      <div className="space-y-1">
                        <AdminDetailFieldLabel>Interaction ID</AdminDetailFieldLabel>
                        {renderCopyableOverviewValue(
                          'interactionId',
                          interactionIdDisplay,
                          'Interaction ID'
                        )}
                      </div>
                      <div className="space-y-1">
                        <AdminDetailFieldLabel>Interaction Type</AdminDetailFieldLabel>
                        <AdminDetailFieldValue>{interactionTypeDisplay}</AdminDetailFieldValue>
                      </div>
                      {renderOverviewLinkedField({
                        label: 'Interaction',
                        valueText: 'View',
                        enabled: Boolean(interactionViewPath),
                        to: interactionViewPath || undefined,
                      })}
                    </div>

                    {/* Column 4 — Review Summary */}
                    <div className="space-y-4">
                      <div className="space-y-1">
                        <AdminDetailFieldLabel>Review ID</AdminDetailFieldLabel>
                        <div className="flex items-center gap-2 min-w-0">
                          <AdminDetailFieldValue as="div" className="truncate min-w-0">
                            {displayReviewId || '—'}
                          </AdminDetailFieldValue>
                          {displayReviewId && displayReviewId !== '—' ? (
                            <button
                              type="button"
                              onClick={handleCopyReviewId}
                              className="inline-flex items-center justify-center shrink-0 text-mediumgray hover:text-primaryblue"
                              title={reviewIdCopied ? 'Copied' : 'Copy Review ID'}
                              aria-label={reviewIdCopied ? 'Copied' : 'Copy Review ID'}
                            >
                              <i
                                className={`fi ${
                                  reviewIdCopied ? 'fi-rr-check text-freshgreen' : 'fi-rr-copy'
                                } text-xs leading-none`}
                                aria-hidden
                              />
                            </button>
                          ) : null}
                        </div>
                      </div>
                      <div className="space-y-1">
                        <AdminDetailFieldLabel>Rating</AdminDetailFieldLabel>
                        <AdminDetailFieldValue as="div" className="inline-flex items-center">
                          {ratingValue != null ? (
                            <>
                              {ratingValue}
                              <span className="text-warmyellow text-sm leading-none">★</span>
                            </>
                          ) : (
                            '—'
                          )}
                        </AdminDetailFieldValue>
                      </div>
                      <div className="space-y-1">
                        <AdminDetailFieldLabel>Status</AdminDetailFieldLabel>
                        {review.status ? (
                          <AdminStatusBadge status={review.status} label={review.status} />
                        ) : (
                          <AdminDetailFieldValue>—</AdminDetailFieldValue>
                        )}
                      </div>
                      <div className="space-y-1">
                        <AdminDetailFieldLabel>Posted On</AdminDetailFieldLabel>
                        <AdminDetailFieldValue>
                          {formatHeaderDateTime(review.postedAt || review.submittedAt)}
                        </AdminDetailFieldValue>
                      </div>
                      <div className="space-y-1">
                        <AdminDetailFieldLabel>Last Updated</AdminDetailFieldLabel>
                        <AdminDetailFieldValue>
                          {formatHeaderDateTime(review.lastUpdated)}
                        </AdminDetailFieldValue>
                      </div>
                      {reviewImageCount > 0 ? (
                        <div className="space-y-1">
                          <AdminDetailFieldLabel>Review Image</AdminDetailFieldLabel>
                          <AdminDetailFieldValue className="tabular-nums">
                            {reviewImageCount}
                          </AdminDetailFieldValue>
                        </div>
                      ) : null}
                      <div className="space-y-1">
                        <AdminDetailFieldLabel>Edit Until</AdminDetailFieldLabel>
                        <AdminDetailFieldValue>{editUntilDisplay}</AdminDetailFieldValue>
                      </div>
                      <div className="space-y-1">
                        <AdminDetailFieldLabel>Reply Status</AdminDetailFieldLabel>
                        <AdminDetailFieldValue>{businessReplyStatus}</AdminDetailFieldValue>
                      </div>
                      {renderOverviewLinkedField({
                        label: 'Open Flags',
                        valueText: String(openFlagsCount),
                        enabled: true,
                        onNavigate: () => setActiveTab('flags'),
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'thread' && (
              <div className="space-y-6">
                {/* Customer Review (Primary Entry) */}
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <AdminStandardAvatar
                      src={review.reviewer?.avatar || Profile}
                      alt={review.reviewer?.name || 'Customer'}
                      onError={(event) => {
                        event.currentTarget.src = Profile;
                      }}
                    />
                    <div>
                      <AdminDetailFieldValue>{review.reviewer?.name || '—'}</AdminDetailFieldValue>
                      {displayReviewId && displayReviewId !== '—' ? (
                        <AdminMetadataText as="div" className="inline-flex items-center gap-1.5 mt-0.5">
                          <span>Review ID {displayReviewId}</span>
                          <button
                            type="button"
                            onClick={handleCopyReviewId}
                            className="inline-flex items-center justify-center text-mediumgray hover:text-primaryblue"
                            title={reviewIdCopied ? 'Copied' : 'Copy Review ID'}
                            aria-label={reviewIdCopied ? 'Copied' : 'Copy Review ID'}
                          >
                            <i
                              className={`fi ${reviewIdCopied ? 'fi-rr-check text-freshgreen' : 'fi-rr-copy'} text-xs leading-none`}
                              aria-hidden
                            />
                          </button>
                        </AdminMetadataText>
                      ) : null}
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <AdminMetadataText as="div" className="inline-flex items-center">
                      {ratingValue != null ? (
                        <>
                          {ratingValue}
                          <span className="text-warmyellow text-sm leading-none">★</span>
                        </>
                      ) : (
                        '—'
                      )}
                    </AdminMetadataText>
                    <AdminMetadataText as="span" className="hidden sm:inline text-mediumgray">
                      •
                    </AdminMetadataText>
                    <AdminMetadataText>
                      Posted On {formatDate(review.postedAt)}
                    </AdminMetadataText>
                  </div>

                  <AdminMainText className="text-offblack max-w-3xl whitespace-pre-wrap">
                    {review.fullText || review.title || '—'}
                  </AdminMainText>

                  {(review.attachments || []).length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                      {review.attachments.map((imageUrl, index) => (
                        <button
                          key={imageUrl}
                          type="button"
                          className="relative rounded-lg overflow-hidden border border-lightgray focus:outline-none focus:ring-2 focus:ring-primaryblue"
                          onClick={() => setPreviewImage({ url: imageUrl, index })}
                        >
                          <img
                            src={imageUrl}
                            alt={`review-media-${index + 1}`}
                            className="h-32 w-full object-cover transition-transform duration-200 hover:scale-105"
                          />
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Business Reply (Secondary Entry) */}
                <div className="space-y-4 pt-6 border-t border-lightgray">
                  {businessReplyStatus === 'Not Replied' ? (
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Reply Status</AdminDetailFieldLabel>
                      <AdminDetailFieldValue>Not Replied</AdminDetailFieldValue>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <AdminStandardAvatar
                          src={review.business?.logo || BusinessFallback}
                          alt={review.business?.name || 'Business'}
                          onError={(event) => {
                            event.currentTarget.src = BusinessFallback;
                          }}
                        />
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <AdminDetailFieldValue>
                              {review.business?.name || '—'}
                            </AdminDetailFieldValue>
                            {businessReplyStatus === 'Deleted' ? (
                              <AdminStatusBadge status="Deleted" label="Deleted" />
                            ) : null}
                          </div>
                          {replyIdDisplay ? (
                            <AdminMetadataText as="div" className="inline-flex items-center gap-1.5 mt-0.5">
                              <span>Review Reply ID {replyIdDisplay}</span>
                              <button
                                type="button"
                                onClick={() =>
                                  handleCopyOverviewField('replyId', replyIdDisplay)
                                }
                                className="inline-flex items-center justify-center text-mediumgray hover:text-primaryblue"
                                title={
                                  copiedOverviewField === 'replyId'
                                    ? 'Copied'
                                    : 'Copy Review Reply ID'
                                }
                                aria-label={
                                  copiedOverviewField === 'replyId'
                                    ? 'Copied'
                                    : 'Copy Review Reply ID'
                                }
                              >
                                <i
                                  className={`fi ${
                                    copiedOverviewField === 'replyId'
                                      ? 'fi-rr-check text-freshgreen'
                                      : 'fi-rr-copy'
                                  } text-xs leading-none`}
                                  aria-hidden
                                />
                              </button>
                            </AdminMetadataText>
                          ) : null}
                        </div>
                      </div>

                      <AdminMetadataText>
                        Replied {formatDate(review.reply?.timestamp || review.reply?.repliedAt)}
                      </AdminMetadataText>

                      <AdminMainText className="text-offblack max-w-3xl whitespace-pre-wrap">
                        {review.reply?.body || '—'}
                      </AdminMainText>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'flags' && (
              <div className="min-w-0 max-w-full">
                <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
                  <div className="w-full min-w-0 max-w-full">
                    <div
                      className="grid min-w-0 py-3 text-offblack border-b border-lightgray gap-x-2 sm:gap-x-3 gap-y-2 px-4"
                      style={{ gridTemplateColumns: FLAGS_TABLE_GRID }}
                    >
                      <AdminDetailTableHeader className="min-w-0 break-words">Flag ID</AdminDetailTableHeader>
                      <AdminDetailTableHeader className="min-w-0 break-words">Case ID</AdminDetailTableHeader>
                      <AdminDetailTableHeader className="min-w-0 break-words">Review Target</AdminDetailTableHeader>
                      <AdminDetailTableHeader className="min-w-0 break-words">Source</AdminDetailTableHeader>
                      <AdminDetailTableHeader className="min-w-0 break-words">Reason</AdminDetailTableHeader>
                      <AdminDetailTableHeader className="min-w-0 break-words">Severity</AdminDetailTableHeader>
                      <AdminDetailTableHeader className="min-w-0 break-words">Case Status</AdminDetailTableHeader>
                      <AdminDetailTableHeader className="min-w-0 break-words leading-tight">Created On</AdminDetailTableHeader>
                      <AdminDetailTableHeader className="min-w-0 break-words leading-tight">Last Updated</AdminDetailTableHeader>
                      <AdminDetailTableHeader className="min-w-0 break-words">Action</AdminDetailTableHeader>
                    </div>

                    {paginatedFlagRows.length > 0 ? (
                      paginatedFlagRows.map((flag) => (
                        <div
                          key={flag.key}
                          className="grid min-w-0 py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-x-2 sm:gap-x-3 gap-y-2 items-start"
                          style={{ gridTemplateColumns: FLAGS_TABLE_GRID }}
                        >
                          <AdminTableBody className="min-w-0 break-words leading-snug">{flag.flagId}</AdminTableBody>
                          <AdminTableBody className="min-w-0 break-words break-all leading-snug">
                            {flag.handlingRecord || '—'}
                          </AdminTableBody>
                          <AdminTableBody className="min-w-0 break-words leading-snug">
                            {flag.reviewTarget}
                          </AdminTableBody>
                          <AdminTableBody className="min-w-0 break-words leading-snug">
                            {flag.source || '—'}
                          </AdminTableBody>
                          <AdminTableBody
                            className="min-w-0 break-words break-all leading-snug"
                            title={flag.reasonText || ''}
                          >
                            {flag.reasonText}
                          </AdminTableBody>
                          <AdminTableBody
                            emphasis
                            className={`min-w-0 break-words leading-snug ${getFlagSeverityTextClass(
                              flag.severity
                            )}`.trim()}
                          >
                            {flag.severity || '—'}
                          </AdminTableBody>
                          <div className="flex items-start min-w-0">
                            <AdminFlagStatusBadge status={flag.handlingStatus} />
                          </div>
                          <AdminTableBody className="min-w-0 break-words leading-snug">
                            {formatFlagsDateTime(flag.createdAt)}
                          </AdminTableBody>
                          <AdminTableBody className="min-w-0 break-words leading-snug">
                            {formatFlagsDateTime(flag.updatedAt)}
                          </AdminTableBody>
                          <div className="flex items-start min-w-0">
                            {flag.actionLabel && flag.actionPath ? (
                              <button
                                type="button"
                                className="text-left bg-transparent border-0 p-0 cursor-pointer"
                                onClick={() => handleFlagRowAction(flag)}
                              >
                                <AdminTableBody className="text-offblack underline-offset-2 hover:underline">
                                  {flag.actionLabel}
                                </AdminTableBody>
                              </button>
                            ) : (
                              <AdminTableBody>—</AdminTableBody>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="py-8 text-center">
                        <AdminSupportingText className="text-mediumgray">No flags found</AdminSupportingText>
                      </div>
                    )}
                  </div>
                </DashboardMainContainer>
                <ConfigurationListPaginationFooter
                  show={flagsTablePagination.showPagination}
                  rangeStart={flagsTablePagination.rangeStart}
                  rangeEnd={flagsTablePagination.rangeEnd}
                  totalCount={reviewFlagRows.length}
                  currentPage={flagsTablePagination.effectivePage}
                  totalPages={flagsTablePagination.totalPages}
                  onPrev={() => setFlagsCurrentPage((prev) => Math.max(1, prev - 1))}
                  onNext={() =>
                    setFlagsCurrentPage((prev) =>
                      Math.min(flagsTablePagination.totalPages, prev + 1)
                    )
                  }
                  goToPage={setFlagsCurrentPage}
                />
              </div>
            )}

            {previewImage && (
              <div
                className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4"
                onClick={() => setPreviewImage(null)}
              >
                <div
                  className="relative max-w-4xl w-full"
                  onClick={(event) => event.stopPropagation()}
                >
                  <button
                    type="button"
                    className="absolute -top-10 right-0 text-white text-sm uppercase tracking-wide"
                    onClick={() => setPreviewImage(null)}
                  >
                    Close
                  </button>
                  <div className="relative rounded-lg overflow-hidden bg-white">
                    <img
                      src={previewImage.url}
                      alt={`Review attachment preview ${previewImage.index + 1}`}
                      className="w-full object-contain max-h-[80vh]"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </DashboardMainContainer>
      </div>
      </div>
    </AdminLayout>
  );
};

export default ReviewDetail;





